import React, { useMemo } from 'react';
import { AlertTriangle, ClipboardCheck, PlusCircle, RotateCcw, XCircle } from 'lucide-react';
import { Quote } from '../../types';
import { Button } from '../Button';
import {
  effectiveTranches,
  formatEur,
  formatQty,
  itemOrderStates,
  orderableQuoteItems,
  quoteOrderSummary,
} from '../../services/quoteOrderTranches';

/**
 * Beauftragungsstand eines (teil)beauftragten Angebots: Summen, Stand je
 * Position und die Liste der Auftragsteile. Bei `partial` mit den Aktionen
 * „Rest beauftragen" und „Rest entfällt"; ein Teil, der noch nicht im
 * Projekt-LV steht, bekommt „Erneut eintragen".
 *
 * Reine Anzeige — gerechnet wird in quoteOrderTranches.
 */
interface Props {
  quote: Quote;
  busy?: boolean;
  onOrderRest?: () => void;
  onDeclineRest?: () => void;
  onRetry?: (trancheId: string) => void;
  /**
   * Der Editor bietet „Nachtrag erstellen" an: bei `partial` steht neben
   * „Rest beauftragen" der Unterschied (die beiden werden verwechselt).
   */
  supplementHint?: boolean;
}

const REST_VS_NACHTRAG = 'Rest beauftragen = schon angebotene Mengen; Nachtrag = zusätzlicher Umfang';

const fmtDay = (iso?: string) => (iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('de-DE') : '—');

export const QuoteOrderPanel: React.FC<Props> = ({ quote, busy, onOrderRest, onDeclineRest, onRetry, supplementHint }) => {
  const summary = useMemo(() => quoteOrderSummary(quote), [quote]);
  const tranches = useMemo(() => effectiveTranches(quote), [quote]);
  const states = useMemo(() => itemOrderStates(quote), [quote]);
  const items = useMemo(() => orderableQuoteItems(quote.items), [quote.items]);
  const partial = quote.status === 'partial';
  const isLegacy = !(quote.orderTranches && quote.orderTranches.length > 0);

  // Altbestand (vor der Teilbeauftragung voll angenommen): nichts zu zeigen,
  // was der grüne Hinweis darüber nicht schon sagt.
  if (isLegacy) return null;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <ClipboardCheck className="w-5 h-5 text-teal-600" />
        <h3 className="text-sm font-bold text-slate-800">Beauftragung</h3>
        <div className="ml-auto flex flex-wrap gap-2">
          {partial && onOrderRest && (
            <Button variant="primary" onClick={onOrderRest} disabled={busy}
                    className="!bg-emerald-600 hover:!bg-emerald-700 border-b-2 border-emerald-700 h-9 text-xs"
                    icon={<PlusCircle className="w-4 h-4" />}
                    title={supplementHint ? REST_VS_NACHTRAG : undefined}>
              Rest beauftragen
            </Button>
          )}
          {partial && onDeclineRest && (
            <Button variant="secondary" onClick={onDeclineRest} disabled={busy} className="h-9 text-xs"
                    icon={<XCircle className="w-4 h-4" />}>
              Rest entfällt
            </Button>
          )}
        </div>
      </div>
      {partial && onOrderRest && supplementHint && (
        <p className="text-xs text-slate-500 -mt-2">{REST_VS_NACHTRAG} (über „Nachtrag erstellen“).</p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          ['Beauftragt', summary.orderedNet, 'text-emerald-700'],
          ['Offen', summary.openNet, partial ? 'text-teal-700' : 'text-slate-400'],
          ['Entfallen', summary.declinedNet, 'text-slate-500'],
        ].map(([label, value, cls]) => (
          <div key={label as string} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500">{label as string}</p>
            <p className={`mt-0.5 text-lg font-bold tabular-nums ${cls as string}`}>{formatEur(value as number)}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-500 -mt-2">Netto, Angebot gesamt {formatEur(quote.netTotal)}.</p>

      {summary.pendingCount > 0 && (
        <div className="flex gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            {summary.pendingCount === 1 ? 'Ein Auftragsteil steht' : `${summary.pendingCount} Auftragsteile stehen`} noch nicht im
            Projekt-LV. Bitte „Erneut eintragen“.
          </span>
        </div>
      )}

      {/* Stand je Position */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
              <th className="text-left py-2 pr-3 font-bold">Position</th>
              <th className="text-right py-2 px-2 font-bold">Angebot</th>
              <th className="text-right py-2 px-2 font-bold">Beauftragt</th>
              <th className="text-right py-2 px-2 font-bold">Offen</th>
              <th className="text-right py-2 pl-2 font-bold">Entfällt</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((it) => {
              const s = states.get(it.id);
              if (!s) return null;
              return (
                <tr key={it.id}>
                  <td className="py-1.5 pr-3 text-slate-700">
                    <span className="font-mono text-xs text-slate-400 mr-2">{it.posNumber || it.lvPosition}</span>
                    {it.title}
                  </td>
                  <td className="py-1.5 px-2 text-right tabular-nums text-slate-500 whitespace-nowrap">{formatQty(s.offered)} {it.unit}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums font-semibold text-emerald-700">{formatQty(s.ordered)}</td>
                  <td className={`py-1.5 px-2 text-right tabular-nums ${s.open > 0 ? 'font-semibold text-teal-700' : 'text-slate-300'}`}>{formatQty(s.open)}</td>
                  <td className="py-1.5 pl-2 text-right tabular-nums text-slate-400">{s.declined > 0 ? formatQty(s.declined) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Auftragsteile */}
      <div className="space-y-2">
        {tranches.map((t) => (
          <div key={t.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm">
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${t.kind === 'decline' ? 'bg-slate-100 text-slate-600' : 'bg-emerald-100 text-emerald-700'}`}>
              Teil {t.seq} · {t.kind === 'decline' ? 'entfällt' : 'beauftragt'}
            </span>
            <span className="text-slate-600">{fmtDay(t.orderDate)}</span>
            {t.customerRef && <span className="text-slate-500">Bestellung {t.customerRef}</span>}
            <span className="font-semibold tabular-nums text-slate-800">{formatEur(t.netAmount)}</span>
            {t.byName && <span className="text-xs text-slate-400">erfasst von {t.byName}</span>}
            {t.note && <span className="basis-full text-xs text-slate-500">{t.note}</span>}
            {t.kind === 'order' && !t.appliedAt && (
              <span className="ml-auto flex items-center gap-2">
                <span className="text-xs font-semibold text-amber-700">nicht im Projekt-LV</span>
                {onRetry && (
                  <Button variant="secondary" onClick={() => onRetry(t.id)} disabled={busy} className="h-8 text-xs"
                          icon={<RotateCcw className="w-3.5 h-3.5" />}>
                    Erneut eintragen
                  </Button>
                )}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
